import React from 'react';
import { Zap, Shield, Users, BarChart } from 'lucide-react';

const features = [
  {
    name: 'AI-Powered Contract Analysis',
    description: 'Leverage advanced AI to analyze contracts, identify opportunities, and improve win rates',
    icon: Zap,
    image: 'https://images.unsplash.com/photo-1664575602276-acd073f104c1?q=80&w=2940&auto=format&fit=crop',
    color: 'from-purple-500 to-indigo-500',
  },
  {
    name: 'Compliance Automation',
    description: 'Automated compliance checks and real-time alerts to meet all government requirements',
    icon: Shield,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2940&auto=format&fit=crop',
    color: 'from-indigo-500 to-purple-500',
  },
  {
    name: 'Team Collaboration',
    description: 'Seamless collaboration with real-time updates and centralized communication',
    icon: Users,
    image: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=2940&auto=format&fit=crop',
    color: 'from-purple-400 to-purple-600',
  },
  {
    name: 'Performance Analytics',
    description: 'Detailed insights and analytics to track progress and optimize your bidding strategy',
    icon: BarChart,
    image: 'https://images.unsplash.com/photo-1507925921958-8a62f3d1a50d?q=80&w=2940&auto=format&fit=crop',
    color: 'from-indigo-400 to-purple-500',
  },
];

export function Features() {
  return (
    <div className="relative overflow-hidden bg-gradient-to-b from-white to-purple-50 py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="bg-gradient-to-r from-purple-600 to-purple-900 bg-clip-text text-3xl font-bold tracking-tight text-transparent sm:text-4xl">
            Powerful Features for Government Contractors
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            Everything you need to streamline your contracting process and increase win rates.
          </p>
        </div>

        <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            {features.map((feature) => (
              <div
                key={feature.name}
                className="group relative overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-purple-900/5 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
              >
                <div className="relative aspect-[16/9] overflow-hidden">
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-90`} />
                  <img
                    src={feature.image}
                    alt={feature.name}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                </div>

                <div className="relative p-8">
                  <div className="flex items-center space-x-3">
                    <div className="rounded-xl bg-purple-100 p-3">
                      <feature.icon className="h-6 w-6 text-purple-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">{feature.name}</h3>
                  </div>
                  <p className="mt-4 text-gray-600">{feature.description}</p>
                  <div className="mt-6">
                    <button className="inline-flex items-center text-sm font-semibold text-purple-600 hover:text-purple-500">
                      Learn more
                      <span className="ml-2 text-xl transition-transform group-hover:translate-x-1">→</span>
                    </button>
                  </div>
                </div>

                <div className="absolute -bottom-4 -right-4 h-32 w-32 rounded-full bg-gradient-to-br from-purple-100 to-purple-50 opacity-50 blur-2xl transition-all duration-300 group-hover:opacity-70"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}